import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ErrorEventDto, QueryResult } from '@flowqa/shared';

type ErrorEventApi = Omit<ErrorEventDto, 'createdAt'> & { createdAt: string | Date };

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const mapErrorEvent = (event: ErrorEventApi): ErrorEventDto => ({
  ...event,
  createdAt: event.createdAt instanceof Date ? event.createdAt : new Date(event.createdAt),
});

export const queryErrorEvents = async (
  input: GridQueryInput,
  tokenOverride?: string | null,
): Promise<QueryResult<ErrorEventDto>> => {
  const authToken = ensureToken(tokenOverride);
  try {
    const response = await apiClient.post<QueryResult<ErrorEventApi>>(
      'superuser/error-events/query',
      {
        page: input.page,
        pageSize: input.pageSize,
        sorts: input.sorts,
        filters: input.filters,
        search: input.search,
      },
      { retries: 1, token: authToken },
    );
    return {
      ...response,
      items: response.items.map(mapErrorEvent),
    };
  } catch (err) {
    throw toApiError(err);
  }
};
